<template>
  <div class="nlp__faq-box">
    <h2> FAQ </h2>
    <div class="nlp__faq-content">
      <div
        v-for="(item, index) in faqData"
        :key="index"
        class="nlp__faq-item"
        :class="{ active: openIndex === index }"
      >
        <div
          class="nlp__faq-item-header"
          @click="toggleFaq(index)"
        >
          <div class="nlp__faq-item-header-title">
            {{ item.title }}
          </div>
          <div class="nlp__faq-item-header-toggle" />
        </div>
        <div
          class="nlp__faq-text"
          v-html="item.content"
        />
      </div>
    </div>
    <div class="nlp__button-box">
      <p>Still unsure?</p>
      <NlpButton
        primary
        text="Try Now"
        @button-click="$router.push({ name: 'source-of-funds' })"
      />
    </div>
  </div>
</template>

<script>
// Components
import NlpButton from '@/pages/landing-page/ui-components/NlpButton.vue'
// Vuex
import { mapState } from 'vuex'

export default {
  name: 'NlpFaq',
  components: {
    NlpButton,
  },
  data() {
    return {
      openIndex: null,
    }
  },
  computed: {
    ...mapState('analytics', ['currencyList']),
    faqData() {
      const currencyLabels =
        this.currencyList?.map(c =>
          c.symbol ? `${c.label} (${c.symbol})` : c.label,
        ) || []

      const formattedCurrencies =
        currencyLabels.length === 0
          ? ''
          : currencyLabels.length === 1
          ? currencyLabels[0]
          : `${currencyLabels.slice(0, -1).join(', ')} and ${currencyLabels.at(
              -1,
            )}`

      return [
        {
          title: 'What does this tool do?',
          content: `<p>SatoshiEye helps you check whether a crypto wallet address is connected to scams, hacks, sanctions, or other suspicious activity.</p> <br> <p>You don’t need any crypto knowledge to use the SatoshiEye tool: just paste the wallet address and we’ll handle the rest.</p>`,
        },
        {
          title: 'What blockchains are supported?',
          content: `<p>SatoshiEye checks ${formattedCurrencies}.</p>`,
        },
        {
          title: 'Can I use it on mobile?',
          content:
            '<p>Yes, SatoshiEye is fully mobile-friendly. You can use it like an app and check wallet addresses anytime, directly from your phone.</p>',
        },
        {
          title: 'Why should I check a wallet address?',
          content:
            '<p>If you receive crypto from a wallet linked to scams, hacks, or sanctioned platforms, your exchange account could be frozen, your funds blocked, or you could even lose your money. A quick wallet check helps you avoid these problems before they happen.</p>',
        },
        {
          title: 'Is this legal to use?',
          content:
            '<p>Yes, checking public blockchain data is completely legal and safe. You remain responsible for using the service in compliance with applicable laws.</p>',
        },
        {
          title: 'What are Reports and Data Refreshes in the subscriptions?',
          content:
            '<p>In SatoshiEye, a report provides a complete overview of an address’s risk level. It includes general address information, who is behind the address when this information is available, an AML risk score, and a money trail showing related entities and labels.</p> <br> <p>A Data Refresh is used to update the report with the latest available data</p>',
        },
        {
          title: 'How does subscription billing work?',
          content:
            '<p>SatoshiEye subscriptions are billed in advance on a recurring monthly or annual basis. You’ll see the price, billing period, included usage, and next billing date at checkout and in your subscription confirmation email. Your subscription will keep renewing unless you cancel before the next billing date.</p>',
        },
        {
          title: 'How do I cancel my subscription?',
          content:
            '<p>You can cancel your subscription at any time in your Account Settings or using the link in our billing emails. Unless we clearly state otherwise, your cancellation takes effect at the end of the current billing period. After that, your subscription won’t renew and you won’t be charged again.</p>',
        },
        {
          title: 'What happens if my payment fails?',
          content:
            '<p>If a renewal payment fails, we may ask you to update your payment method and may temporarily suspend paid features until the payment is completed. If the payment is not completed within the period stated in our notice, we may terminate your subscription.</p> <br> <p>Here’s how access may change during the 8-day period:</p> <br> <p><em>Day 0–3</em><br>Restriction: Access to new Reports generation and data refreshes is paused.<br>Permission: You can still maintain full access to view and download existing Reports generated before the payment failure.</p> <br> <p><em>Day 4–7</em><br>Total Restriction: Access to all premium features, including the archive of previously generated Reports, is paused.</p> <br> <p><em>Day 8</em><br>If the payment is still not completed, your subscription may be permanently cancelled.</p>',
        },
        {
          title: 'Do unused reports roll over?',
          content:
            '<p>No. Unless we clearly state otherwise for a specific subscription, unused Reports expire at the end of your current billing period and don’t roll over. Your Report limit refreshes every month, unless otherwise stated.</p>',
        },
        {
          title: 'Do you store my card details?',
          content:
            '<p>No, we don’t store your full card details.</p> <br> <p>Payments are securely processed by Stripe, our payment service provider. Depending on how you check out, you may enter your payment details on a Stripe-hosted checkout page or into Stripe-hosted payment fields embedded in our checkout. Your sensitive payment details go straight to Stripe and never pass through our servers.</p> <br> <p>If you start a paid subscription or choose to save your payment method, Stripe may save your payment method for future subscription renewals or other authorized charges, in line with your agreement with us and Stripe\'s applicable terms and privacy notice.</p> <br> <p>We may receive limited, non-sensitive payment information from Stripe - such as your card brand, last four digits, expiration date, and payment or billing status information. We use this information for billing, customer support, fraud prevention, and compliance.</p> <br> <p>We never store your full card number or your card verification code (CVV/CVC) after payment authorization.</p>',
        },
        {
          title: 'Can I get a refund?',
          content:
            '<p>Unless the law requires otherwise or our Terms of Service explicitly say differently, subscription fees already paid for the current billing period are non-refundable. We also do not provide refunds for any unused time or unused Reports.</p>',
        },
        {
          title: 'Can I upgrade or downgrade my Subscription Plan?',
          content:
            '<p>Yes.</p> <br> <p><strong>Upgrading Your Plan (Upgrade)</strong></p> <br> <p>Payment: You pay the Full Price of the new plan immediately. Any remaining days or reports from your old plan are discarded.<br>Billing Cycle: Your billing month "restarts" today. Your next payment will be scheduled exactly one month from now.<br>Reports: Your old report balance is cleared, and you receive the full quota of your new plan instantly.<br>Access: Higher-tier features are unlocked the moment the payment is successful.</p> <br> <p><strong>Switching from Monthly to Annual</strong></p> <br> <p>Payment: You pay the Full Price of the Annual Plan today.<br>Billing Cycle: Your new "Yearly Anniversary" starts today.<br>Reports: You receive a fresh quota immediately. This quota will automatically refill every month on the same day of the month you upgraded.</p> <br> <p><strong>Lowering Your Plan (Downgrade)</strong></p> <br> <p>Payment: You do not pay anything at the moment you request a downgrade. No refunds are issued for the current period.<br>Access: You keep full access to your current (higher) plan and all its reports until the end of your current paid period.<br>Switch: The system only switches you to the lower plan and charges the lower price when your current month or year expires.</p> <br> <p><strong>Switching from Annual to Monthly</strong></p> <br> <p>You remain on the Annual plan with all its benefits until the year is up.<br>On the anniversary date, instead of charging you for another year, the system will switch you to the monthly rate.</p> <br> <p><strong>Upgrading during Trial (Standard → Pro)</strong></p> <br> <p>For users who want to try a higher tier before the trial ends.</p> <br> <p>Payment: No immediate charge. You stay in "Trial Mode."<br>The Deal: You are essentially telling the system: "On Day 8, please charge me for Pro instead of Standard."<br>Access: Higher-tier features (like Pro modules) are unlocked immediately so you can test them.<br>Trial Clock: The 7-day timer does not restart. If you upgrade on Day 4, you still only have 3 days left of the free trial.<br>Reports: You do not get extra reports. You keep the remaining balance of your original trial quota.</p> <br> <p><strong>Downgrading during Trial</strong></p> <br> <p>Trial Ends Instantly: The free trial is terminated immediately the moment you confirm the downgrade.<br>Immediate Payment: You are charged the full price of the new lower plan right away.<br>New Start Date: Today becomes your new "Billing Anniversary."<br>Reports & Access: Trial-only "Pro" features are locked instantly. Your report quota is reset to the full amount of the new paid plan (e.g., you move from 5 trial reports to 75 paid reports).</p>',
        },
      ]
    },
  },
  methods: {
    toggleFaq(index) {
      this.openIndex = this.openIndex === index ? null : index
    },
  },
}
</script>

<style scoped>
.nlp__faq-box h2 {
  margin-bottom: 60px;
}

.nlp__faq-item {
  display: grid;
  grid-template-rows: fit-content(100%) 0fr;
  padding: 20px 24px;
  background-color: #fff;
  border: 1px solid #e2e9f6;
  border-radius: 24px;
  transition: grid-template-rows 0.3s ease;
  overflow: hidden;
}
.nlp__faq-item + .nlp__faq-item {
  margin-top: 16px;
}
.nlp__faq-item-header {
  display: flex;
  gap: 16px;
  cursor: pointer;
}
.nlp__faq-item-header-title {
  flex: 1;
  font-weight: 700;
  font-size: 20px;
  line-height: 28px;
  color: #1c1c1c;
}
.nlp__faq-item-header-toggle {
  width: 20px;
  height: 20px;
  margin-top: 4px;
  background-color: transparent;
  border-radius: 6px;
  position: relative;
  transition: background-color 0.3s ease;
}
.nlp__faq-item-header-toggle:before,
.nlp__faq-item-header-toggle:after {
  content: '';
  background-color: #5a627d;
  border-radius: 4px;
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
}
.nlp__faq-item-header-toggle:before {
  width: 12px;
  height: 2px;
}
.nlp__faq-item-header-toggle:after {
  width: 2px;
  height: 12px;
  transition: transform 0.3s ease;
}
.nlp__faq-text {
  opacity: 0;
  overflow: hidden;
  transition: opacity 0.3s ease;
  font-weight: 400;
  line-height: 26px;
}
.nlp__faq-text p {
  font-size: 16px;
}
.nlp__faq-text p:first-child {
  padding-top: 8px;
}
.nlp__faq-text p + p {
  margin-top: 16px;
}
.nlp__faq-item.active {
  grid-template-rows: fit-content(100%) 1fr;
}
.nlp__faq-item.active .nlp__faq-text {
  opacity: 1;
}
.nlp__faq-item.active .nlp__faq-item-header-toggle {
  background-color: #f4f6f9;
}
.active .nlp__faq-item-header-toggle:after {
  transform: translate(-50%, -50%) rotate(270deg);
}

@media (max-width: 767px) {
  .nlp__faq-item {
    padding: 20px 16px;
  }
  .nlp__faq-item + .nlp__faq-item {
    margin-top: 8px;
  }
}
</style>
